import React, { FC, useRef, ReactNode, useMemo, useCallback } from "react";
import useWatchState from "../../../hooks/useWatchState";
import { ITabContext, TabSwitcherContext } from "./TabContext";

export interface ITabsContainerProps {
  /**
   * Sets an active tab to display
   */
  activeItem?: string;

  /**
   * Check new tab key in list of existing tabs or not
   */
  checkAvailable?: boolean;
  children?: ReactNode;
  /**
   * If not null component will behave as controlled,
   * If undefined, component will manage active tabs itself
   */
  onSelect?(itemId: string): void;
}

const TabsContainer: FC<ITabsContainerProps> = (props) => {
  const { activeItem, checkAvailable = true, children, onSelect } = props;

  const [activeItemState, setActiveItemState] = useWatchState<string>({
    value: activeItem,
  });

  const availableTabs = useRef([]);

  const handleSelect = useCallback(
    (itemKey: string) => {
      /**
       * Check if `itemKey` is present in available tabs array
       * To prevent redirection to non existing tabs
       */
      if (checkAvailable && !availableTabs.current.includes(itemKey)) return;

      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      onSelect ? onSelect(itemKey) : setActiveItemState(itemKey);
    },
    [checkAvailable, onSelect, setActiveItemState],
  );

  const createTab = useCallback((tabId: string | string[]) => {
    availableTabs.current = availableTabs.current.concat(tabId);
  }, []);
  const removeTab = useCallback((tabId: string | string[]) => {
    availableTabs.current = availableTabs.current.filter(
      (item) => ![].concat(tabId).includes(item),
    );
  }, []);

  const context: ITabContext = useMemo(
    () => ({
      activeItem: activeItemState,
      onSelect: handleSelect,
      createTab,
      removeTab,
    }),
    [activeItemState, createTab, handleSelect, removeTab],
  );

  return (
    <TabSwitcherContext.Provider data-testid="tab-context" value={context}>
      {children}
    </TabSwitcherContext.Provider>
  );
};

export default TabsContainer;
